/**
 * The chat log, laid out like Foundry's PF2e chat: each action is a card (who, what, against
 * whom, action cost) holding its dice rolls and results. Reactions nest inside the card that
 * triggered them. Rolls show the d20, modifier, total, DC and a colored degree of success;
 * damage rolls show each die and a chip per damage type.
 */
import type { ChatCardView, LogLineView, RollView } from '../shared/view.ts';
import { glyph, refAttr } from './rules.ts';

function esc(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

const signed = (n: number) => (n >= 0 ? `+${n}` : `–${Math.abs(n)}`);

const TONE_ICON: Record<string, string> = {
  combat: '⚔', damage: '✦', heal: '✚', light: '☼', danger: '⚠', level: '★', loot: '◈', system: '›', info: '·',
  speech: '❝', npc: '❝', narration: '✧',
};

const DEGREE_NAMES = ['Critical Failure', 'Failure', 'Success', 'Critical Success'];
const ATTACK_NAMES = ['Critical Miss', 'Miss', 'Hit', 'Critical Hit'];

/** "a hit", "a critical failure": a degree of success as the roll calls it. */
export function degreePhrase(r: RollView, degree: number): string {
  return `a ${((r.kind === 'attack' ? ATTACK_NAMES : DEGREE_NAMES)[degree] ?? '').toLowerCase()}`;
}

/** Good or bad news for the party: a modifier that helped whoever rolled. */
export function matterTone(r: RollView, helped: boolean): 'good' | 'bad' | '' {
  return r.side === 'world' ? '' : helped === (r.side === 'party') ? 'good' : 'bad';
}

/** "off-guard –2 AC", "MAP –5": a deciding modifier, and where it applied. */
export function matterLabel(r: RollView, m: NonNullable<RollView['mattered']>[number]): string {
  return `${m.label} ${signed(m.value)}${m.dc ? ` ${r.against?.label ?? 'DC'}` : ''}`;
}

/**
 * PF2e "modifiers matter": the modifiers without which the result would have been different
 * (green when that was good news for the party, red when it wasn't).
 */
function matteredHtml(r: RollView): string {
  if (!r.mattered?.length || r.degree === undefined) return '';
  const chips = r.mattered.map((m) => `<span class="mm ${matterTone(r, m.helped)}" title="${esc(`Without it: ${degreePhrase(r, m.without)}`)}">${esc(matterLabel(r, m))}</span>`).join('');
  const was = [...new Set(r.mattered.map((m) => degreePhrase(r, m.without)))].join(' or ');
  return `<div class="cr-matter"><span class="mm-ic" title="Modifiers that mattered">⚖</span>${chips}<span class="mm-what">${esc(degreePhrase(r, r.degree))} instead of ${esc(was)}</span></div>`;
}

type Node =
  | { kind: 'card'; card: ChatCardView | null; seq: number; rows: Node[] }
  | { kind: 'line'; line: LogLineView };

/** Groups log lines into cards (nesting reaction cards inside the card that triggered them). */
export function buildChat(log: readonly LogLineView[]): Node[] {
  const top: Node[] = [];
  const cards = new Map<string, Extract<Node, { kind: 'card' }>>();
  for (const line of log) {
    if (line.kind === 'card' && line.card) {
      const node = { kind: 'card' as const, card: line.card, seq: line.seq, rows: [] as Node[] };
      cards.set(line.card.id, node);
      const parent = line.card.parent ? cards.get(line.card.parent) : undefined;
      (parent ? parent.rows : top).push(node);
      continue;
    }
    let owner = line.group ? cards.get(line.group) : undefined;
    if (!owner && line.group) {
      // The card's header scrolled out of the window; keep its rows together anyway.
      owner = { kind: 'card', card: null, seq: line.seq, rows: [] };
      cards.set(line.group, owner);
      top.push(owner);
    }
    if (owner) owner.rows.push({ kind: 'line', line });
    else if (line.kind === 'roll' && line.roll) top.push({ kind: 'card', card: null, seq: line.seq, rows: [{ kind: 'line', line }] });
    else top.push({ kind: 'line', line });
  }
  return top;
}

function labelHtml(label: string): string {
  const m = /^(.*?)(\s\(.*\))$/.exec(label);
  return m ? `${esc(m[1])}<span class="paren">${esc(m[2])}</span>` : esc(label);
}

function diceHtml(r: RollView): string {
  if (r.dice.length > 10) {
    const sum = r.dice.reduce((s, d) => s + d.value, 0);
    return `<span class="die many" title="${esc(r.dice.map((d) => d.value).join(' + '))}">${r.dice.length}d${r.dice[0].sides}: ${sum}</span>`;
  }
  return r.dice.map((d) => {
    const nat = d.sides === 20 ? (d.value === 20 ? 'nat20' : d.value === 1 ? 'nat1' : '') : '';
    return `<span class="die d${d.sides} ${nat}" title="d${d.sides}">${d.value}</span>`;
  }).join('');
}

function degreeBadge(r: RollView): string {
  if (r.degree === undefined && !r.outcome) return '';
  const text = r.outcome ?? DEGREE_NAMES[r.degree!];
  return `<span class="deg ${r.degree !== undefined ? `deg-${r.degree}` : ''}">${esc(text)}</span>`;
}

function rollRow(r: RollView, standalone: boolean): string {
  const label = r.short ?? r.label;
  if (r.kind === 'initiative') {
    return `<div class="cr roll init"><span class="cr-label">${labelHtml(label)}</span><span class="cr-detail">${esc(r.detail ?? '')}</span></div>`;
  }
  const heal = r.kind === 'heal';
  const math = [
    diceHtml(r),
    r.modifier ? `<span class="mod">${signed(r.modifier)}</span>` : '',
    r.multiplier && r.multiplier !== 1 ? `<span class="mult" title="Doubled">×${r.multiplier}</span>` : '',
    `<span class="eq">=</span><b class="total ${heal ? 'heal' : ''}">${r.total}</b>`,
    r.against ? `<span class="vs">vs ${esc(r.against.label)} ${r.against.value}</span>` : '',
  ].join('');
  const chips = heal
    ? `<span class="dmg t-healing">✚ ${r.total} HP</span>`
    : (r.damage ?? []).map((d) => `<span class="dmg t-${esc(d.type)}">${d.amount} ${esc(d.type)}</span>`).join('');
  const notes = r.notes?.length ? `<span class="cr-notes">${r.notes.map((n) => `<span class="note">${esc(n)}</span>`).join('')}</span>` : '';
  const who = standalone && r.detail ? `<span class="cr-who">${esc(r.detail)}</span>` : '';
  return `<div class="cr roll k-${esc(r.kind)}">
    <span class="cr-label">${labelHtml(label)}${who}</span>
    <span class="cr-math">${math}</span>
    <span class="cr-out">${chips}${degreeBadge(r)}</span>
    ${notes}${matteredHtml(r)}
  </div>`;
}

function lineRow(l: LogLineView, inCard: boolean): string {
  const icon = TONE_ICON[l.tone] ?? '·';
  if (l.tone === 'speech' || l.tone === 'npc') {
    // "Name: “words”" becomes a speech line with the speaker's name set apart.
    const i = l.text.indexOf(': ');
    const who = i > 0 ? l.text.slice(0, i) : '';
    const said = i > 0 ? l.text.slice(i + 2) : l.text;
    return `<div class="chat-line speech tone-${esc(l.tone)}"><span class="ic">${icon}</span><span><b class="spk">${esc(who)}</b> <span class="said">${esc(said)}</span></span></div>`;
  }
  return `<div class="${inCard ? 'cr text' : 'chat-line'} tone-${esc(l.tone)}"><span class="ic">${icon}</span><span>${esc(l.text)}</span></div>`;
}

function cardHead(c: ChatCardView): string {
  const title = `<span class="cc-title"${refAttr(c.ref)}>${esc(c.title)}</span>${c.cost !== undefined ? glyph(c.cost) : ''}`;
  return `<div class="cc-head">
    ${c.actor ? `<span class="cc-actor">${esc(c.actor)}</span>` : ''}${title}
    ${c.target ? `<span class="cc-target"><span class="arrow">➜</span>${esc(c.target)}</span>` : ''}
  </div>`;
}

function nodeHtml(n: Node, depth: number): string {
  if (n.kind === 'line') {
    const l = n.line;
    if (l.kind === 'roll' && l.roll) return rollRow(l.roll, false);
    return lineRow(l, depth > 0);
  }
  const first = n.rows[0];
  const lone = !n.card && first?.kind === 'line' && first.line.kind === 'roll' ? first.line.roll : undefined;
  const side = n.card?.side ?? lone?.side ?? 'world';
  const head = n.card ? cardHead(n.card) : lone ? `<div class="cc-head"><span class="cc-title">${esc(lone.label)}</span></div>` : '';
  const rows = lone && n.rows.length === 1 ? rollRow(lone, true) : n.rows.map((r) => nodeHtml(r, depth + 1)).join('');
  return `<div class="cc side-${esc(side)} ${depth ? 'nested' : ''}">${head}${rows ? `<div class="cc-body">${rows}</div>` : ''}</div>`;
}

/** What each log element last showed (the game window's log and the pop-out each have one). */
const shown = new WeakMap<HTMLElement, string>();

export function renderChat(el: HTMLElement, log: readonly LogLineView[]): void {
  const key = `${log[0]?.seq ?? 0}:${log.at(-1)?.seq ?? 0}`;
  if (shown.get(el) === key) return;
  shown.set(el, key);
  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  el.innerHTML = buildChat(log).map((n) => nodeHtml(n, 0)).join('');
  if (atBottom) el.scrollTop = el.scrollHeight;
}
